import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { BehaviorSubject, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FormMode } from 'src/app/controls/common/form-mode';
import { IRole } from 'src/app/models/interfaces/role.interface';
import { MemoryService } from 'src/app/services/memory.service';
import { RolesService } from 'src/app/services/roles.service';
import { RoleDetailContext } from '../role-detail-context';
import { RoleFormComponent } from '../role-form/role-form.component';
import { RoleRightsFormComponent } from '../role-rights-form/role-rights-form.component';

@Component({
    selector: 'app-role-detail',
    templateUrl: './role-detail.component.html',
    styleUrls: ['./role-detail.component.scss'],
    imports: [MatTabsModule, RoleFormComponent, RoleRightsFormComponent]
})
export class RoleDetailComponent implements OnInit {
    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;

    /**
     * Reference au composant groupe d'onglets.
     */
    @ViewChild(MatTabGroup)
    tabGroup: MatTabGroup;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    @Output()
    public roleUpdated: EventEmitter<IRole> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    @Output()
    public roleRightsUpdated: EventEmitter<IRole> = new EventEmitter();

    /**
     * Emis lors d'un import
     */
    @Output()
    public roleRightsImported: EventEmitter<number> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    /**
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /**
     * Contexte du formulaire.
     */
    public _context: RoleDetailContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<RoleDetailContext> = new BehaviorSubject(null);

    /**
     * Valeur indiquant si l'utilisateur connecté est le super-administrateur.
     */
    public isSuperAdmin: boolean;

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: RoleDetailContext) {
        // set the latest value for _data BehaviorSubject
        this._context$.next(value);
    }

    private SuperAdminId: number = 0;

    constructor(
        private rolesService: RolesService,
        memoryService: MemoryService
    ) {
        this.isSuperAdmin = memoryService.getConnectedUser().roles.indexOf(this.SuperAdminId) !== -1;
    }

    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        /**
         * S'abonne aux changements de contexte
         */
        this._context$.subscribe((x) => {
            this._context = x;
        });
    }

    /**
     * Appellé lorsque des changements sur le formulaire "infos" sont sauvegardés.
     * @param role: Infos du role modifié.
     */
    public onRoleUpdated(role: IRole) {
        this.roleUpdated.emit(role);
    }

    /**
     * Appellé lorsque des changements sur le formulaire "infos" sont sauvegardés.
     * @param role: Infos du role modifié.
     */
    public onRoleRightsUpdated(role: IRole) {
        this.roleRightsUpdated.emit(role);
    }

    public onRoleRightsExported() {
        this.rolesService
            .exportRights(this._context.editedItemId)
            .pipe(
                catchError((err) => {
                    console.error('Export Error');
                    return of(null);
                })
            )
            .subscribe((blob) => {
                const a = document.createElement('a');
                const objectUrl = URL.createObjectURL(blob);
                a.href = objectUrl;
                a.download = `Droits du rôle ${this._context.editedItemId}.xlsx`;
                a.click();
                URL.revokeObjectURL(objectUrl);
            });
    }

    public onRoleRightsImported() {
        this.roleRightsImported.emit(this._context.editedItemId);
    }

    /**
     * Appellé lorsque des changements sur le formulaire "infos" sont annulés.
     * @param user: Infos de l'article modifié.
     */
    public onCanceled(value: boolean) {
        this.canceled.emit(value);
    }
}
